import { useCallback, useEffect, useState } from 'react'
import {
  Typography,
  Space,
  Button,
  Popconfirm,
  App,
  Empty,
  Input,
  Table,
  Dropdown,
  Select,
  Spin
} from 'antd'
import type { Breakpoint } from 'antd/es/_util/responsiveObserver'
import { FileText, Trash2, Plus, ArrowRight, Search, Edit3, MoreHorizontal, Copy } from 'lucide-react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import { useDocumentStorage } from '../../hooks/useDocumentStorage'
import { formatDate } from '../../utils/date'
import { UpgradeBanner } from './UpgradeBanner'
import { useCompanyStore } from '../../store/company/companyStore'
import { DocumentSummary } from '../../services/storage/documents/types'
import { useApi } from '../../services/ApiContext'
import { PaginatedResponse } from '../../services/types'

const { Text, Title } = Typography

const sortOptions = [
  {
    value: 'updatedAt',
    label: 'Recently Updated',
    field: 'updatedAt',
    order: -1
  },
  {
    value: 'createdAt',
    label: 'Recently Created',
    field: 'createdAt',
    order: -1
  },
  { value: 'projectTitle_asc', label: 'Name (A-Z)', field: 'projectTitle', order: 1 },
  { value: 'projectTitle_desc', label: 'Name (Z-A)', field: 'projectTitle', order: -1 }
]

export const DocumentList = () => {
  const { message } = App.useApp()
  const { deleteDocument, newDocument } = useDocumentStorage()
  const [documents, setDocuments] = useState<Array<DocumentSummary>>([])
  const [searchText, setSearchText] = useState('')
  const [debouncedSearchText, setDebouncedSearchText] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [sortBy, setSortBy] = useState('updatedAt')
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null)
  const api = useApi()
  const company = useCompanyStore((state) => state.company)
  const navigate = useNavigate()

  // Debounce search text
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText)
    }, 300) // 300ms delay

    return () => clearTimeout(timer)
  }, [searchText])

  const loadDocuments = useCallback(async () => {
    setLoading(true)
    try {
      const sortOption = sortOptions.find((option) => option.value === sortBy)
      const docs = (await api.findDocuments({
        $limit: pageSize,
        $select: ['_id', 'id', 'title', 'projectTitle', 'updatedAt', 'createdAt'],
        $sort: sortOption ? { [sortOption.field]: sortOption.order } : { updatedAt: -1 },
        $skip: (currentPage - 1) * pageSize,
        $search: debouncedSearchText
      }) as unknown as PaginatedResponse<DocumentSummary>)
      setDocuments(docs.data)
      setTotal(docs.total)
    } catch (error) {
      message.error('Failed to load documents')
    } finally {
      setLoading(false)
    }
  }, [currentPage, pageSize, debouncedSearchText, sortBy])

  useEffect(() => {
    loadDocuments()
  }, [loadDocuments])

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearchText, sortBy])

  const handleDelete = async (document: DocumentSummary) => {
    try {
      await deleteDocument(document._id)
      await loadDocuments()
      message.success('Document deleted successfully')
    } catch {
      message.error('Failed to delete document')
    }
  }

  const handleDuplicate = async (doc: DocumentSummary) => {
    setDuplicatingId(doc._id)
    try {
      const result = await api.duplicateDocument(doc._id)
      message.success('Document duplicated')
      navigate(`/app/editor/${result._id}`)
    } catch {
      message.error('Failed to duplicate document')
    } finally {
      setDuplicatingId(null)
    }
  }

  const shouldUpgrade = documents.length > 0 && company.plan === 'free'

  const columns = [
    {
      title: 'Project',
      key: 'projectTitle',
      onCell: (doc: DocumentSummary) => ({
        onClick: () => {
          navigate(`/app/editor/${doc._id}`)
        },
        style: {
          cursor: 'pointer'
        }
      }),
      render: (doc: DocumentSummary) => (
        <Space>
          <FileText className="h-5 w-5 text-primary" />
          <div>
            <Text strong className="block">
              {doc.projectTitle || 'Untitled Project'}
            </Text>
          </div>
        </Space>
      )
    },
    {
      title: 'Last Updated',
      key: 'updatedAt',
      responsive: ['md' as Breakpoint],
      onCell: (doc: DocumentSummary) => ({
        onClick: () => {
          navigate(`/app/editor/${doc._id}`)
        },
        style: {
          cursor: 'pointer'
        }
      }),
      render: (doc: DocumentSummary) => (
        <Text type="secondary">
          {formatDate(doc.updatedAt)}
        </Text>
      )
    },
    {
      title: '',
      key: 'actions',
      align: 'right' as const,
      width: 50,
      render: (doc: DocumentSummary) => {
        const menuItems = [
          {
            key: 'edit',
            label: (
              <Link to={`/app/editor/${doc._id}`}>
                <Space>
                  <Edit3 className="h-4 w-4" />
                  Edit
                </Space>
              </Link>
            )
          },
          {
            key: 'duplicate',
            label: (
              <Space>
                {duplicatingId === doc._id ? (
                  <Spin size="small" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
                Duplicate
              </Space>
            ),
            disabled: duplicatingId === doc._id,
            onClick: () => handleDuplicate(doc)
          },
          {
            key: 'delete',
            label: (
              <Popconfirm
                title="Delete document"
                description="Are you sure you want to delete this document?"
                arrow={false}
                onConfirm={() => handleDelete(doc)}
                okText="Yes"
                cancelText="No"
              >
                <Space>
                  <Trash2 className="h-4 w-4" />
                  Delete
                </Space>
              </Popconfirm>
            ),
            danger: true
          }
        ]

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button type="text" icon={<MoreHorizontal className="h-4 w-4" />} />
          </Dropdown>
        )
      }
    }
  ]

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <Title level={4}>FF&E Schedules</Title>
      </div>

      {/* {shouldUpgrade && <UpgradeBanner />} */}

      {/* Search and Sort Bar */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6 items-start sm:items-center">
        <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
          <Input
            prefix={<Search className="h-4 w-4 text-gray-400" />}
            placeholder="Search projects..."
            allowClear
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="w-full sm:max-w-md"
          />

          <Select
            value={sortBy}
            onChange={setSortBy}
            options={sortOptions}
            className="w-full sm:w-[200px]"
          />
        </div>

        <div className="w-full sm:w-auto sm:ml-auto">
          <Link
            onClick={() => {
              newDocument()
            }}
            to="/app/editor"
            className="block w-full sm:w-auto"
          >
            <Button
              // disabled={shouldUpgrade}
              type="primary"
              icon={<Plus className="h-4 w-4" />}
              className="w-full sm:w-auto"
            >
              <span className="inline">New Schedule</span>
            </Button>
          </Link>
        </div>
      </div>

      {/* Documents Table */}
      {documents.length > 0 && (
        <div>
          <Table
            dataSource={documents}
            columns={columns}
            rowKey="_id"
            loading={loading}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total,
              showSizeChanger: false,
              showQuickJumper: false,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total}`,
              onChange: (page, size) => {
                setCurrentPage(page)
                setPageSize(size || 10)
              },
              pageSizeOptions: ['10', '20', '50', '100'],
              size: 'small',
              responsive: true
            }}
            className="bg-white rounded-lg shadow-sm"
          />
        </div>
      )}

      {documents.length === 0 && !loading && (
        <Empty
          className="mb-10"
          description={
            searchText
              ? `No documents found matching "${searchText}"`
              : "Get your projects done faster by building FF&E schedules in SprintSpec, you can later manage or search them here."
          }
        >
          {!searchText && (
            <NavLink
              onClick={() => {
                if (!shouldUpgrade) newDocument()
              }}
              to="/app/editor"
            >
              <Button size="large" type="primary">
                Create FF&E Specification{' '}
                <ArrowRight className="h-4 w-4"></ArrowRight>
              </Button>
            </NavLink>
          )}
        </Empty>
      )}
      {loading && <Spin className="flex justify-center items-center h-full" />}
    </div>
  )
}
